import { setAppScale } from './appScale';

export function usePinchZoom(minScale = 0.35, maxScale = 1.0) {
    const scale = ref(1);
    const isZooming = ref(false);
    let initialDistance = 0;
    let initialScale = 1;
    let isGestureActive = false;
    let hideIndicatorTimeout: ReturnType<typeof setTimeout> | null = null;

    const getDistance = (touches: TouchList): number => {
        const dx = touches[0].clientX - touches[1].clientX;
        const dy = touches[0].clientY - touches[1].clientY;
        return Math.sqrt(dx * dx + dy * dy);
    };

    const applyScale = (newScale: number) => {
        setAppScale(newScale);

        // Save to localStorage for persistence
        try {
            localStorage.setItem('app-zoom-scale', newScale.toString());
        } catch (e) {
            console.warn('Failed to save zoom scale to localStorage', e);
        }
    };

    const showIndicator = () => {
        isZooming.value = true;
        if (hideIndicatorTimeout) {
            clearTimeout(hideIndicatorTimeout);
        }
    };

    const hideIndicator = () => {
        if (hideIndicatorTimeout) {
            clearTimeout(hideIndicatorTimeout);
        }
        hideIndicatorTimeout = setTimeout(() => {
            isZooming.value = false;
        }, 1000);
    };

    const handleTouchStart = (e: TouchEvent) => {
        if (e.touches.length === 2) {
            // Prevent native browser zoom
            e.preventDefault();
            isGestureActive = true;
            initialDistance = getDistance(e.touches);
            initialScale = scale.value;
            showIndicator();
        }
    };

    const handleTouchMove = (e: TouchEvent) => {
        if (e.touches.length === 2 && isGestureActive) {
            e.preventDefault();
            const currentDistance = getDistance(e.touches);
            const scaleChange = currentDistance / initialDistance;
            let newScale = initialScale * scaleChange;

            // Range limiting with smooth deceleration at boundaries
            if (newScale < minScale) {
                newScale = minScale + (newScale - minScale) * 0.3;
            } else if (newScale > maxScale) {
                newScale = maxScale + (newScale - maxScale) * 0.3;
            }

            // Round to 2 decimal places for stability
            newScale = Math.round(newScale * 100) / 100;

            scale.value = newScale;
            applyScale(newScale);
        }
    };

    const handleTouchEnd = (e: TouchEvent) => {
        if (e.touches.length < 2) {
            if (isGestureActive) {
                // Apply final value with constraints
                let finalScale = scale.value;
                finalScale = Math.max(minScale, Math.min(maxScale, finalScale));
                scale.value = finalScale;
                applyScale(finalScale);
                hideIndicator();
            }
            isGestureActive = false;
            initialDistance = 0;
        }
    };

    const initializeScale = () => {
        // Restore saved scale on load
        try {
            const savedScale = localStorage.getItem('app-zoom-scale');
            if (savedScale) {
                const parsedScale = parseFloat(savedScale);
                if (!isNaN(parsedScale) && parsedScale >= minScale && parsedScale <= maxScale) {
                    scale.value = parsedScale;
                    applyScale(parsedScale);
                    return;
                }
            }
        } catch (e) {
            console.warn('Failed to load zoom scale from localStorage', e);
        }

        // Default initialization
        applyScale(1);
    };

    onMounted(() => {
        // Initialize scale
        initializeScale();

        // Add listeners with passive: false for preventDefault
        document.addEventListener('touchstart', handleTouchStart, { passive: false });
        document.addEventListener('touchmove', handleTouchMove, { passive: false });
        document.addEventListener('touchend', handleTouchEnd, { passive: false });
        document.addEventListener('touchcancel', handleTouchEnd, { passive: false });
    });

    onBeforeUnmount(() => {
        if (hideIndicatorTimeout) {
            clearTimeout(hideIndicatorTimeout);
        }
        document.removeEventListener('touchstart', handleTouchStart);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
        document.removeEventListener('touchcancel', handleTouchEnd);
    });

    const resetScale = () => {
        scale.value = 1;
        applyScale(1);
    };

    return {
        scale,
        isZooming,
        resetScale,
    };
}
